// Centralized helper for resolving the relationship between users (parents)
// and athletes. During the parentUserIds migration we support BOTH the legacy
// `parentUserId` (single UID string) and the new `parentUserIds` (array of UIDs).
//
// All consumer pages (Profile, UserProfile, CheckIn, etc.) should use these
// helpers instead of querying directly. After the one-time backfill that
// populates parentUserIds on every athlete, we can drop the legacy field.

import { collection, getDocs, query, where } from 'firebase/firestore';

/**
 * Load all athletes linked to a user, by either legacy `parentUserId` or
 * new `parentUserIds` array. Deduplicates by doc id.
 *
 * @param {Firestore} db
 * @param {string} uid - The user UID
 * @returns {Promise<Array>} Athlete objects (with `id` field)
 */
export async function loadLinkedAthletes(db, uid) {
  if (!uid) return [];
  try {
    const [legacy, modern] = await Promise.all([
      getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', uid))),
      getDocs(query(collection(db, 'athletes'), where('parentUserIds', 'array-contains', uid))),
    ]);
    const map = new Map();
    legacy.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
    modern.docs.forEach(d => map.set(d.id, { id: d.id, ...d.data() }));
    return [...map.values()];
  } catch (e) {
    console.error('loadLinkedAthletes error:', e);
    return [];
  }
}

/**
 * Check whether a UID is a parent on an athlete doc.
 * Returns true if uid matches `parentUserId` OR is in `parentUserIds` array.
 */
export function isParentOf(athleteData, uid) {
  if (!uid || !athleteData) return false;
  if (athleteData.parentUserId === uid) return true;
  if (Array.isArray(athleteData.parentUserIds) && athleteData.parentUserIds.includes(uid)) return true;
  return false;
}

/**
 * Return the canonical array of parent UIDs for an athlete doc, merging both fields.
 * Use this when displaying or iterating over all parents.
 */
export function getAllParentUids(athleteData) {
  if (!athleteData) return [];
  const set = new Set();
  if (athleteData.parentUserId) set.add(athleteData.parentUserId);
  if (Array.isArray(athleteData.parentUserIds)) {
    athleteData.parentUserIds.forEach(id => id && set.add(id));
  }
  return [...set];
}

/**
 * Build the field updates to add a parent to an athlete. Writes BOTH fields
 * for backward compat: parentUserId becomes the first entry if it's blank,
 * and parentUserIds always includes the new UID.
 *
 * Use with updateDoc(athleteRef, addParentUpdates(athleteData, newUid)).
 */
export function addParentUpdates(athleteData, newUid) {
  if (!newUid) return {};
  const existing = Array.isArray(athleteData?.parentUserIds) ? athleteData.parentUserIds : [];
  if (existing.includes(newUid)) return {};
  const updated = [...existing, newUid];
  const updates = { parentUserIds: updated };
  // If legacy field is empty, populate it so old code still works.
  if (!athleteData?.parentUserId) updates.parentUserId = newUid;
  return updates;
}

/**
 * Build the field set for a NEW athlete doc. Always populates BOTH fields.
 * Use this in Register.js, AddPerson.js, etc. when creating athletes.
 */
export function newAthleteParentFields(parentUid) {
  if (!parentUid) return { parentUserId: null, parentUserIds: [] };
  return { parentUserId: parentUid, parentUserIds: [parentUid] };
}
